import { useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { cn } from '@/lib/utils/cn';

export interface AreaSeries {
  key: string;
  label: string;
  values: number[];
  lineColor: string;
  areaColor: string;
}

interface GlowAreaChartProps {
  series: AreaSeries[];
  labels: string[];
  height?: number;
  className?: string;
}

const PAD = { top: 20, right: 16, bottom: 32, left: 48 } as const;
const TENSION = 0.35;

function buildBezierPath(pts: ReadonlyArray<{ x: number; y: number }>): string {
  if (pts.length === 0) return '';
  if (pts.length === 1) return `M${pts[0]!.x},${pts[0]!.y}`;
  let d = `M${pts[0]!.x},${pts[0]!.y}`;
  for (let i = 1; i < pts.length; i++) {
    const p0 = pts[i - 2] ?? pts[i - 1]!;
    const p1 = pts[i - 1]!;
    const p2 = pts[i]!;
    const p3 = pts[i + 1] ?? pts[i]!;
    const cp1x = p1.x + (p2.x - p0.x) * TENSION;
    const cp1y = p1.y + (p2.y - p0.y) * TENSION;
    const cp2x = p2.x - (p3.x - p1.x) * TENSION;
    const cp2y = p2.y - (p3.y - p1.y) * TENSION;
    d += ` C${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`;
  }
  return d;
}

export function GlowAreaChart({
  series,
  labels,
  height = 220,
  className,
}: GlowAreaChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svgWidth, setSvgWidth] = useState(600);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setSvgWidth(Math.max(200, el.clientWidth));
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setSvgWidth(Math.max(200, entry.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const n = labels.length;
  if (n === 0) return null;

  const cw = Math.max(10, svgWidth - PAD.left - PAD.right);
  const ch = Math.max(10, height - PAD.top - PAD.bottom);
  const bottom = PAD.top + ch;

  const allVals = series.flatMap((s) => s.values);
  const maxVal = Math.max(...allVals, 1);

  const xOf = (i: number): number =>
    PAD.left + (n <= 1 ? cw / 2 : (i / (n - 1)) * cw);
  const yOf = (v: number): number => PAD.top + ch - (v / maxVal) * ch;

  function makePts(vals: number[]) {
    return vals.map((v, i) => ({ x: xOf(i), y: yOf(v) }));
  }

  function linePath(vals: number[]): string {
    return buildBezierPath(makePts(vals));
  }

  function areaPath(vals: number[]): string {
    const pts = makePts(vals);
    if (pts.length === 0) return '';
    const line = buildBezierPath(pts);
    const last = pts[pts.length - 1]!;
    const first = pts[0]!;
    return `${line} L${last.x},${bottom} L${first.x},${bottom} Z`;
  }

  const yTicks = [0, 0.25, 0.5, 0.75, 1] as const;
  const labelStep = n <= 7 ? 1 : n <= 14 ? 2 : n <= 30 ? 4 : 7;

  function handleMouseMove(e: MouseEvent<SVGSVGElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - rect.left - PAD.left;
    setHoverIdx(Math.max(0, Math.min(n - 1, Math.round((mx / cw) * (n - 1)))));
  }

  const hx = hoverIdx !== null ? xOf(hoverIdx) : null;

  return (
    <div
      ref={containerRef}
      className={cn('relative w-full select-none', className)}
      style={{ height }}
    >
      {/* Floating tooltip */}
      {hoverIdx !== null && hx !== null && (
        <div
          className="pointer-events-none absolute z-20 min-w-[152px] rounded-xl border border-white/[0.08] text-xs shadow-2xl"
          style={{
            background: 'rgba(5,12,28,0.97)',
            backdropFilter: 'blur(20px)',
            padding: '10px 12px',
            top: 8,
            left:
              hx > svgWidth * 0.65
                ? Math.max(4, hx - 168)
                : Math.min(hx + 14, svgWidth - 168),
          }}
        >
          <p
            className="mb-2 text-[11px] font-semibold tracking-wide"
            style={{ color: 'rgba(255,255,255,0.6)' }}
          >
            {labels[hoverIdx]}
          </p>
          {series.map((s) => (
            <div key={s.key} className="flex items-center justify-between gap-4 py-0.5">
              <span
                className="flex items-center gap-1.5 text-[11px]"
                style={{ color: 'rgba(255,255,255,0.4)' }}
              >
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.lineColor }} />
                {s.label}
              </span>
              <span
                className="text-[11px] font-bold tabular-nums"
                style={{ color: s.lineColor }}
              >
                {(s.values[hoverIdx] ?? 0).toLocaleString()}
              </span>
            </div>
          ))}
          <div
            className="mt-2 flex justify-between border-t pt-2"
            style={{ borderColor: 'rgba(255,255,255,0.08)' }}
          >
            <span style={{ color: 'rgba(255,255,255,0.3)' }}>Total</span>
            <span className="font-bold text-white tabular-nums">
              {series.reduce((t, s) => t + (s.values[hoverIdx] ?? 0), 0).toLocaleString()}
            </span>
          </div>
        </div>
      )}

      <svg
        width={svgWidth}
        height={height}
        viewBox={`0 0 ${svgWidth} ${height}`}
        className="overflow-visible"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoverIdx(null)}
        role="img"
        aria-label="Activity area chart"
      >
        <defs>
          {series.map((s) => (
            <linearGradient key={`ag-${s.key}`} id={`ag-${s.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.areaColor} stopOpacity="0.52" />
              <stop offset="65%" stopColor={s.areaColor} stopOpacity="0.07" />
              <stop offset="100%" stopColor={s.areaColor} stopOpacity="0" />
            </linearGradient>
          ))}
          {series.map((s) => (
            <filter
              key={`gf-${s.key}`}
              id={`gf-${s.key}`}
              x="-20%"
              y="-100%"
              width="140%"
              height="300%"
            >
              <feGaussianBlur stdDeviation="3.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          ))}
        </defs>

        {/* Y-axis grid */}
        {yTicks.map((pct) => {
          const y = PAD.top + ch * (1 - pct);
          const v = maxVal * pct;
          const lbl =
            v >= 1000
              ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`
              : Math.round(v).toString();
          return (
            <g key={`g-${pct}`}>
              <line
                x1={PAD.left}
                y1={y}
                x2={svgWidth - PAD.right}
                y2={y}
                stroke="rgba(255,255,255,0.06)"
                strokeWidth={pct === 0 ? 1 : 0.5}
                strokeDasharray={pct > 0 ? '3 6' : undefined}
              />
              <text x={PAD.left - 8} y={y + 4} textAnchor="end" fontSize={9} fill="rgba(255,255,255,0.22)">
                {lbl}
              </text>
            </g>
          );
        })}

        {/* Area fills — render reversed so first series renders on top */}
        {[...series].reverse().map((s) => (
          <path
            key={`area-${s.key}`}
            d={areaPath(s.values)}
            fill={`url(#ag-${s.key})`}
            style={{ opacity: entered ? 1 : 0, transition: 'opacity 1.3s ease-out' }}
          />
        ))}

        {/* Lines with glow */}
        {series.map((s) => (
          <path
            key={`line-${s.key}`}
            d={linePath(s.values)}
            fill="none"
            stroke={s.lineColor}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter={`url(#gf-${s.key})`}
            style={{ opacity: entered ? 1 : 0, transition: 'opacity 0.9s ease-out 0.25s' }}
          />
        ))}

        {/* Hover crosshair + dots */}
        {hoverIdx !== null && hx !== null && (
          <>
            <line
              x1={hx}
              y1={PAD.top}
              x2={hx}
              y2={bottom}
              stroke="rgba(255,255,255,0.12)"
              strokeWidth="1"
              strokeDasharray="3 4"
            />
            {series.map((s) => {
              const v = s.values[hoverIdx] ?? 0;
              const cy = yOf(v);
              return (
                <g key={`dot-${s.key}`}>
                  <circle cx={hx} cy={cy} r={8} fill={s.lineColor} opacity="0.15" />
                  <circle cx={hx} cy={cy} r={3.5} fill={s.lineColor} stroke="white" strokeWidth="1.5" />
                </g>
              );
            })}
          </>
        )}

        {/* X-axis labels */}
        {labels.map((lbl, i) => {
          if (i % labelStep !== 0 && i !== n - 1) return null;
          const isHov = hoverIdx === i;
          return (
            <text
              key={`xl-${i}`}
              x={xOf(i)}
              y={height - 8}
              textAnchor="middle"
              fontSize={9}
              fill={isHov ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.25)'}
            >
              {lbl.length > 5 ? lbl.slice(-5) : lbl}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
